export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-0 border-b border-rule overflow-hidden"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          {/* LEFT — content */}
          <div className="col-span-12 lg:col-span-7 pb-16 md:pb-20">
            <div
              className="inline-flex items-center gap-3 mb-10 animate-fade-in"
              data-testid="hero-availability"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-mustard opacity-60 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-mustard" />
              </span>
              <span className="overline text-ink">
                Available for fractional engagements
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="serif font-light text-ink leading-[0.95] text-[44px] sm:text-6xl md:text-7xl lg:text-[88px] animate-fade-up"
            >
              Scale without
              <br />
              scaling headcount.
            </h1>

            <p
              data-testid="hero-subtext"
              className="mt-8 text-base md:text-lg leading-relaxed text-ink/80 max-w-xl animate-fade-up"
              style={{ animationDelay: "0.2s" }}
            >
              Saran Gandhi &amp; Associates is a boutique strategy and
              operations consultancy. We help founders and operating leaders
              <span className="text-ink"> scale, restructure, and exit </span>
              on their terms.
            </p>

            <div
              className="mt-9 flex flex-col sm:flex-row gap-4 sm:items-center animate-fade-up"
              style={{ animationDelay: "0.35s" }}
            >
              <a
                href="https://calendly.com/sarangandhi1/30min"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-calendly"
                className="inline-flex items-center justify-center gap-3 bg-ink text-paper px-7 py-4 text-sm tracking-wide hover:bg-mustard hover:text-ink transition-colors duration-300 group"
              >
                Book a 30-minute call
                <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </a>
              <a
                href="#exit-readiness"
                data-testid="hero-cta-approach"
                className="inline-flex items-center justify-center gap-3 text-sm tracking-wide text-ink hover:text-mustard transition-colors duration-300 px-2 py-4"
              >
                <span className="link-line">Our services</span>
                <span aria-hidden>↓</span>
              </a>
            </div>

            {/* Stats */}
            <div
              className="mt-14 pt-10 border-t border-rule grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-6 animate-fade-up"
              style={{ animationDelay: "0.5s" }}
              data-testid="hero-stats"
            >
              <Stat number="10" label="EU countries" />
              <Stat number="100k+" label="Operators led" />
              <Stat number="30+" label="Years in ops" />
              <Stat number="1:1" label="Single engagement" />
            </div>
          </div>

          {/* RIGHT — portrait */}
          <div className="col-span-12 lg:col-span-5 lg:pl-6 reveal">
            <div className="relative">
              {/* Mustard offset block */}
              <div className="absolute -bottom-3 -right-3 w-full h-full bg-mustard -z-0 hidden md:block" />
              <div className="relative z-10 overflow-hidden">
                <img
                  src="/images/portrait.jpg"
                  alt="Saran Gandhi, founder"
                  className="w-full aspect-[3/4] object-cover img-editorial"
                  data-testid="hero-portrait"
                />
              </div>
              {/* Caption */}
              <div className="relative z-10 mt-4 flex items-baseline justify-between">
                <div>
                  <div className="serif text-lg text-ink">Saran Gandhi</div>
                  <div className="overline text-slate mt-1">Founder · London</div>
                </div>
                <div className="overline text-slate">Est. 2024</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust bar */}
      <div className="border-t border-rule bg-sand/60">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-10 grid grid-cols-1 md:grid-cols-4 gap-y-6 md:gap-x-12 items-baseline">
          <div className="overline text-slate">Practice background</div>
          <Credential brand="Amazon" detail="Senior Manager · European Ops · 10 countries" />
          <Credential brand="Indian Army" detail="21 years · Corps of EME · Logistics &amp; engineering" />
          <Credential brand="Independent" detail="UK &amp; EU · One engagement at a time" />
        </div>
      </div>
    </section>
  );
}

function Stat({ number, label }) {
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}>
      <div className="serif text-4xl md:text-5xl font-light text-ink tracking-tight leading-none">
        {number}
      </div>
      <div className="overline text-slate mt-2.5 leading-snug">{label}</div>
    </div>
  );
}

function Credential({ brand, detail }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="serif text-lg md:text-xl font-medium text-ink tracking-tight">
        {brand}
      </div>
      <div className="text-xs leading-relaxed text-slate" dangerouslySetInnerHTML={{ __html: detail }} />
    </div>
  );
}
